import { useEffect, useRef, useState } from 'react';
import { PageWrapper } from '@/layout/components/PageWrapper';
import { PageHeader } from '@/layout/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Bot, CheckCircle2, ExternalLink, Plus, Send, ShieldCheck, User as UserIcon, XCircle } from 'lucide-react';
import { MarkdownContent } from '@/components/MarkdownContent';
import { useCurrentSystem } from '@/hooks/useCurrentSystem.tsx';
import {
    useApproveAiCommandMutation,
    useGetAiConversationHistoryQuery,
    useGetAiConversationsQuery,
} from '@/store/aiChatApi';
import { useAiChatStream } from '@/hooks/useAiChatStream';
import { AiActionPreview, AiChatDisplayMessage, AiMessage, AiMessageAction } from '@/types/AiChat';
import { cn } from '@/lib/utils.ts';

export default function AiChatPage() {
    const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
    const [draft, setDraft] = useState('');
    const messagesViewportRef = useRef<HTMLDivElement>(null);
    const { system, systemQuery } = useCurrentSystem();
    const schoolId = system?.id ?? null;

    const { data: conversations = [], refetch: refetchConversations } = useGetAiConversationsQuery(
        schoolId ? { schoolId } : undefined,
        { skip: !schoolId },
    );

    const { data: history } = useGetAiConversationHistoryQuery(selectedConversationId as number, {
        skip: !selectedConversationId,
    });
    const [approveCommand] = useApproveAiCommandMutation();

    const {
        messages, isStreaming, error, conversationId, sendMessage, startNewConversation, loadHistory, dismissError,
    } = useAiChatStream();

    useEffect(() => {
        if (history) {
            const historyMessages: AiChatDisplayMessage[] = history.messages
                .filter((m) => m.role === 'user' || m.role === 'model')
                .map((m) => ({
                    role: m.role as 'user' | 'model',
                    text: m.text,
                    citations: m.citations,
                    action: actionFromHistory(m),
                }));
            loadHistory(history.id, historyMessages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history]);

    // אחרי שהתקבל conversation_id חדש מה-stream (שיחה חדשה) — לרענן את רשימת השיחות בסיידבר
    const prevConversationIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (conversationId && conversationId !== prevConversationIdRef.current) {
            refetchConversations();
        }
        prevConversationIdRef.current = conversationId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    useEffect(() => {
        setSelectedConversationId(null);
        startNewConversation();
    }, [schoolId, startNewConversation]);

    useEffect(() => {
        const viewport = messagesViewportRef.current;
        if (!viewport) return;

        const animationFrame = window.requestAnimationFrame(() => {
            viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
        });
        return () => window.cancelAnimationFrame(animationFrame);
    }, [messages]);

    function handleSelectConversation(id: number) {
        setSelectedConversationId(id);
    }

    function handleNewConversation() {
        setSelectedConversationId(null);
        startNewConversation();
    }

    async function handleSend() {
        const text = draft.trim();
        if (!text || !schoolId || isStreaming) return;
        setDraft('');
        dismissError();
        await sendMessage({ schoolId, message: text, conversationId });
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    }

    async function handleApprove(messageIndex: number, commandId: string) {
        updateMessageAction(messageIndex, { status: 'executing' });
        try {
            const receipt = await approveCommand(commandId).unwrap();
            updateMessageAction(messageIndex, { status: 'applied', receipt });
        } catch (requestError) {
            const errorData = (requestError as { data?: { msg?: string; error_code?: string } }).data;
            updateMessageAction(messageIndex, {
                status: errorData?.error_code === 'STALE_PREVIEW' ? 'conflict' : 'failed',
                error: errorData?.msg || 'ביצוע השינוי נכשל',
            });
        }
    }

    function updateMessageAction(messageIndex: number, actionUpdate: Partial<AiMessageAction>) {
        loadHistory(conversationId as number, messages.map((message, index) => (
            index === messageIndex
                ? { ...message, action: { ...message.action!, ...actionUpdate } }
                : message
        )));
    }

    return (
        <PageWrapper>
            <PageHeader title="עוזר AI תפעולי" />

            <div className="flex items-center gap-3 flex-wrap" dir="rtl">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNewConversation}
                    className="gap-1.5 border-sky-200 bg-white text-sky-800 shadow-sm hover:bg-sky-50"
                >
                    <Plus className="h-4 w-4" />
                    שיחה חדשה
                </Button>
            </div>

            {!schoolId ? (
                <div className="text-center py-20 text-muted-foreground">
                    <p>{systemQuery.isLoading ? 'טוען את המוסד הנוכחי…' : 'לא נמצא מוסד נוכחי'}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]" dir="rtl">
                    <aside className="max-h-[70vh] space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/70 p-2 shadow-sm">
                        <p className="px-3 pb-1 pt-2 text-xs font-medium text-slate-500">שיחות קודמות</p>
                        {conversations.length === 0 && (
                            <p className="text-xs text-muted-foreground p-2">אין עדיין שיחות במוסד זה</p>
                        )}
                        {conversations.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => handleSelectConversation(c.id)}
                                className={cn(
                                    'w-full truncate rounded-md px-3 py-2.5 text-right text-sm text-slate-700 transition-colors hover:bg-white hover:text-sky-800',
                                    selectedConversationId === c.id && 'bg-white font-semibold text-sky-800 shadow-sm ring-1 ring-sky-100',
                                )}
                            >
                                {c.title || `שיחה #${c.id}`}
                            </button>
                        ))}
                    </aside>

                    <section className="flex h-[70vh] min-h-[520px] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div
                            ref={messagesViewportRef}
                            className="min-h-0 flex-1 overflow-y-scroll scroll-smooth bg-gradient-to-b from-sky-50/40 via-white to-emerald-50/30 p-5 [scrollbar-color:#7dd3fc_#f1f5f9] [scrollbar-width:thin]"
                        >
                            {messages.length === 0 && (
                                <div className="text-center text-muted-foreground py-16">
                                    <Bot className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                    <p>שאל/י כל שאלה על המערכת — תיעוד, API, שלוחות ותפעול</p>
                                </div>
                            )}
                            <div className="space-y-5">
                                {messages.map((m, idx) => (
                                    <ChatBubble
                                        key={idx}
                                        message={m}
                                        onApprove={(commandId) => handleApprove(idx, commandId)}
                                    />
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-right text-sm text-red-700" dir="rtl">{error}</div>
                        )}

                        <div className="flex items-end gap-2 border-t border-slate-200 bg-white p-3">
                            <Textarea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="הקלד/י שאלה... (Enter לשליחה, Shift+Enter לשורה חדשה)"
                                dir="rtl"
                                className="min-h-[48px] max-h-40 flex-1 resize-none border-slate-200 bg-slate-50/60 text-right shadow-inner focus-visible:border-sky-300 focus-visible:ring-sky-100"
                                disabled={isStreaming}
                            />
                            <Button
                                onClick={handleSend}
                                disabled={isStreaming || !draft.trim()}
                                className="gap-1.5 bg-sky-700 text-white hover:bg-sky-800"
                            >
                                {isStreaming ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                                שליחה
                            </Button>
                        </div>
                    </section>
                </div>
            )}
        </PageWrapper>
    );
}

function ChatBubble({
    message,
    onApprove,
}: {
    message: AiChatDisplayMessage;
    onApprove: (commandId: string) => void;
}) {
    const isUser = message.role === 'user';
    const displayText = isUser ? message.text : formatAssistantText(message.text);
    return (
        <div className={cn('flex w-full items-start gap-2.5', isUser ? 'justify-start' : 'justify-end')} dir="rtl">
            <div className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border shadow-sm',
                isUser
                    ? 'border-sky-200 bg-sky-100 text-sky-800'
                    : 'border-emerald-200 bg-emerald-100 text-emerald-800',
            )}>
                {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className={cn(
                'max-w-[85%] rounded-lg border px-4 py-3 text-right text-sm leading-6 shadow-sm md:max-w-[78%]',
                isUser
                    ? 'border-sky-200 bg-sky-50 text-slate-900'
                    : 'border-emerald-200 bg-emerald-50/80 text-slate-800',
            )} dir="rtl">
                {isUser ? (
                    <p className="whitespace-pre-wrap text-right">{displayText}</p>
                ) : displayText ? (
                    <MarkdownContent
                        content={displayText}
                        className="text-right text-slate-800 [&_code]:inline-block [&_code]:[direction:ltr] [&_pre]:text-left [&_pre]:[direction:ltr]"
                    />
                ) : (
                    <span className="text-emerald-700">…</span>
                )}
                {message.action && <ActionCard action={message.action} onApprove={onApprove} />}
                {!!message.citations?.length && (
                    <div className="mt-3 flex flex-wrap justify-start gap-2 border-t border-emerald-200/70 pt-2" dir="rtl">
                        {message.citations.map((c, i) => (
                            <CitationLink key={`${c.source_id}-${i}`} citation={c} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ActionCard({ action, onApprove }: { action: AiMessageAction; onApprove: (commandId: string) => void }) {
    const data = action.receipt || action.preview;
    if (!data) return null;
    const target = data.target;
    const isReady = action.status === 'ready' && action.preview?.command_id;
    const isApplied = action.status === 'applied' && action.receipt?.verified;

    return (
        <div className="mt-3 border-t border-emerald-200 pt-3" aria-live="polite">
            <div className="mb-2 flex items-center gap-2 font-semibold text-slate-900">
                {isApplied ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <ShieldCheck className="h-4 w-4 text-sky-700" />}
                <span>
                    {isApplied ? 'בוצע ואומת' : action.status === 'no_changes' ? 'לא נדרש שינוי' : 'תצוגה מקדימה'}
                </span>
            </div>
            {target && <p className="mb-2 text-xs text-slate-600">{target.name} · שלוחה {target.path}</p>}
            {data.changes.map((change) => (
                <div key={change.key} className="mb-2 rounded-md border border-slate-200 bg-white p-2.5">
                    <p className="font-medium text-slate-900">{change.label}</p>
                    <p className="text-xs text-slate-600">
                        {change.before_display} ← {change.after_display}
                    </p>
                </div>
            ))}
            {data.unchanged.map((item) => (
                <p key={item.key} className="mb-1 text-xs text-slate-600">
                    <strong>{item.label}:</strong> כבר מוגדר כ-{item.display}
                </p>
            ))}
            {action.error && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-red-700">
                    <XCircle className="h-4 w-4" />
                    {action.error}
                </p>
            )}
            {isReady && (
                <Button
                    size="sm"
                    className="mt-3 gap-1.5 bg-emerald-700 text-white hover:bg-emerald-800"
                    onClick={() => onApprove(action.preview!.command_id!)}
                >
                    <ShieldCheck className="h-4 w-4" />
                    אישור וביצוע
                </Button>
            )}
            {action.status === 'executing' && <p className="mt-2 text-xs text-sky-700">מבצע ומאמת את השמירה…</p>}
        </div>
    );
}

function actionFromHistory(message: AiMessage): AiMessageAction | undefined {
    if (message.action) return message.action;
    const prepareCall = message.tool_calls?.find((call) => call.name === 'extensions_prepare_update');
    const result = prepareCall?.result as AiActionPreview | undefined;
    if (!result || (result.status !== 'ready' && result.status !== 'no_changes')) return undefined;
    return { preview: result, status: result.status };
}

function formatAssistantText(text: string): string {
    return text
        .replace(/\s*\[מקור:\s*`?sclass:\d+`?\]\s*/gi, ' ')
        .replace(/^\s*`?sclass:\d+`?\s*$/gim, '')
        .trim();
}

function CitationLink({ citation }: { citation: AiChatDisplayMessage['citations'][number] }) {
    if (citation.source_type === 'extension' && citation.id) {
        const label = citation.name
            ? `${citation.name}${citation.path ? ` · שלוחה ${citation.path}` : ''}`
            : `עריכת שלוחה${citation.path ? ` ${citation.path}` : ''}`;
        return (
            <a
                href={`/learning/sclass/${citation.id}/change/`}
                className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-sky-800 shadow-sm ring-1 ring-sky-200 transition-colors hover:bg-sky-50"
                title="פתיחת השלוחה לעריכה"
            >
                <ExternalLink className="h-3.5 w-3.5" />
                {label}
            </a>
        );
    }

    return (
        <Badge variant="secondary" title={citation.excerpt || ''} className="bg-white text-slate-600 ring-1 ring-slate-200">
            {citation.title || 'מקור מידע'}
        </Badge>
    );
}
